import { Plus } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/layout/ui'
import type { ButtonSize, ButtonVariant } from '@/components/layout/ui'
import { cn } from '@/lib/cn'
import { useWorkspace } from '@/lib/workspace'

import { CreateProjectModal } from './CreateProjectModal'

/**
 * The one entry point into project creation, with the dialog it owns.
 *
 * It renders nothing for a role that cannot create a project: a viewer is
 * refused by the API anyway, and an affordance that always fails is worse than
 * none. Every place a person looks for this — the sidebar's project list, the
 * workspace overview, the empty state that used to strand them — drops in the
 * same component, so the flow cannot drift between them.
 */
export function NewProjectButton({
  variant = 'secondary',
  size = 'md',
  label = 'New project',
  iconOnly = false,
  className,
}: {
  variant?: ButtonVariant
  size?: ButtonSize
  label?: string
  /** Renders a compact + control; `label` becomes its accessible name. */
  iconOnly?: boolean
  className?: string
}) {
  const workspace = useWorkspace()
  const [open, setOpen] = useState(false)

  if (!workspace.can('content.write')) return null

  // Note for the mobile drawer: opening this must NOT close the sidebar. The
  // drawer unmounts its whole subtree when it closes, and the dialog lives in
  // it — closing the drawer here would take the dialog with it. Navigating
  // after a successful create closes the drawer anyway.
  const start = () => setOpen(true)

  return (
    <>
      {iconOnly ? (
        // A complete class list rather than size overrides on Button: `cn` does
        // not resolve Tailwind conflicts, so a half-overridden variant would
        // resolve by stylesheet order rather than by intent.
        <button
          type="button"
          aria-label={label}
          title={label}
          onClick={start}
          className={cn(
            'inline-flex size-6 shrink-0 items-center justify-center rounded-md text-ink-500 transition',
            'hover:bg-ink-100 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100',
            className,
          )}
        >
          <PlusGlyph />
        </button>
      ) : (
        <Button variant={variant} size={size} className={className} onClick={start}>
          <PlusGlyph />
          {label}
        </Button>
      )}

      <CreateProjectModal open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function PlusGlyph() {
  return <Plus className="size-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
}
